// The charity mount of the ONE CRM package. Scope + profile words enter here, never in the engine.

import {
  CrmContactsPage,
  CrmDonorDetailPage,
  CrmImportPage,
  CrmSegmentBuilderPage,
  CrmSegmentsPage,
  type CrmModuleConfig,
} from '@givmo/crm';
import { use, type ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button, Card, EmptyState, Icon, Skeleton, testIdProp } from '@givmo/design-system';
import { ModuleGate } from '@givmo/org-console';
import { Building2 } from 'lucide-react';
import { useCharity } from '../grants/use-charity';
import { isOwnerRole } from '../admins/admins-roles';
import { charityCrmVocab } from './crm-vocab';
import { CRM_MODULE_ROUTES } from './crm-routes';
import { TEAM_ACCESS_ROUTE } from '../../console/console-routes';
import {
  CharityConsoleContext,
  useOptionalCharityConsole,
  type CharityConsoleState,
} from '../../console/charity-console-context';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { NotFound } from '../../components/not-found';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useTurnOnHref } from '../../console/use-turn-on-href';

/** The CRM catalog's one write string (backend `charity_module.py`, the `donors` module). */
export const CRM_WRITE_PERMISSION = 'charity_crm:write';

/** The CRM's module slug (backend `OrgModule.DONORS`); its pages live at `/crm`. */
export const CRM_MODULE_SLUG = 'donors';

/**
 * Whether this person may change the organization's contacts. An unsettled standing read (or a
 * mount outside the console) offers the controls: the server refuses on its own, and the panel
 * then says so in plain words.
 */
function useCanEditContacts(): boolean {
  const permissions = use(CharityConsoleContext)?.read.permissions ?? null;
  return permissions === null || permissions.includes(CRM_WRITE_PERMISSION);
}

function useCharityCrmConfig(): {
  config: CrmModuleConfig | null;
  state: ReturnType<typeof useCharity>;
} {
  const state = useCharity();
  const canEdit = useCanEditContacts();
  const canOpenTerms = state.membership !== null && isOwnerRole(state.membership.role);
  return {
    state,
    config:
      state.isReady && state.charityId !== null
        ? {
            orgRef: state.charityId,
            orgName: state.membership?.name,
            vocab: charityCrmVocab,
            canEdit,
            links: {
              contacts: CRM_MODULE_ROUTES.contacts,
              donor: CRM_MODULE_ROUTES.donor,
              segments: CRM_MODULE_ROUTES.segments,
              newSegment: CRM_MODULE_ROUTES.newSegment,
              segment: CRM_MODULE_ROUTES.segment,
              ...(canOpenTerms ? { terms: '/terms' } : {}),
              import: CRM_MODULE_ROUTES.import,
              // Connected AI is a section of Team access (`OrgConnectedAiPanel`).
              connectAi: TEAM_ACCESS_ROUTE,
            },
          }
        : null,
  };
}

/**
 * The console's direct-link gate (`ModuleGate`, `org-console-shell.md` § the five mechanics, row 3):
 * the CRM's pages are built in every environment since EMC-BE21, so whether this organization may
 * open one is the console's ONE standing read, as for every module. Not offered (every charity by
 * default) is the host's not-found; off, locked or setting up answer in place with the request door.
 * Outside the console (a harness that renders a mount alone) there is no standing to read, so the
 * page itself, the rule before the gate.
 */
function CrmModuleGate({ children }: { children: ReactNode }) {
  const standingRead = useOptionalCharityConsole();
  if (standingRead === null) return <>{children}</>;
  return <CrmConsoleModuleGate standingRead={standingRead}>{children}</CrmConsoleModuleGate>;
}

function CrmConsoleModuleGate({
  standingRead,
  children,
}: {
  standingRead: CharityConsoleState;
  children: ReactNode;
}) {
  const { pending, errored, standings, refetch } = standingRead;
  const door = useRequestAccessDoor();
  const navigate = useModuleNavigate();
  const turnOnHref = useTurnOnHref(CRM_MODULE_SLUG);
  const standing = standings?.[CRM_MODULE_SLUG];
  const request = standing?.accessRequest;
  return (
    <ModuleGate
      slug={CRM_MODULE_SLUG}
      label="CRM"
      read={{ pending, errored, standings }}
      onRetry={refetch}
      renderNotFound={() => <NotFound />}
      turnOnHref={turnOnHref}
      onNavigate={navigate}
      onRequestAccess={standing?.held === true ? undefined : () => door.open(CRM_MODULE_SLUG)}
      accessRequest={
        request?.status === 'PENDING'
          ? { status: 'pending' }
          : request?.status === 'DENIED'
            ? { status: 'declined', note: request.resolutionNote ?? null }
            : undefined
      }
    >
      {children}
    </ModuleGate>
  );
}

function CrmMountGate({ children }: { children: (config: CrmModuleConfig) => React.ReactNode }) {
  return (
    <CrmModuleGate>
      <CrmMembershipGate>{children}</CrmMembershipGate>
    </CrmModuleGate>
  );
}

/** The charity this person administers, read before any CRM page (the membership read's states). */
function CrmMembershipGate({
  children,
}: {
  children: (config: CrmModuleConfig) => React.ReactNode;
}) {
  const { config, state } = useCharityCrmConfig();
  if (state.isPending) {
    return (
      <div className="mx-auto w-full max-w-[1120px] px-24 py-24" {...testIdProp('crm-loading')}>
        <Card className="p-24">
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </div>
    );
  }
  if (state.isError) {
    return (
      <EmptyState
        testId="crm-memberships-error"
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Couldn’t load your organization"
        description="We couldn’t load your organization. Please try again."
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={state.refetch}
            testId="crm-memberships-retry"
          >
            Try again
          </Button>
        }
      />
    );
  }
  if (config === null) {
    return (
      <EmptyState
        testId="crm-no-org"
        icon={<Icon icon={Building2} size={20} aria-hidden />}
        title="Claim your organization first"
        description="Once your organization is available, its contacts appear here."
      />
    );
  }
  return children(config);
}

export function CharityCrmContactsMount() {
  // `/crm?contact=<id>` opens on one of the organization's own contacts (a link to it).
  const initialContactId =
    typeof window === 'undefined'
      ? undefined
      : (new URLSearchParams(window.location.search).get('contact') ?? undefined);
  return (
    <CrmMountGate>
      {(config) => <CrmContactsPage config={config} initialContactId={initialContactId} />}
    </CrmMountGate>
  );
}

export function CharityCrmDonorMount() {
  const donorId = useParams({ strict: false }).donorId ?? '';
  return (
    <CrmMountGate>
      {(config) => <CrmDonorDetailPage config={config} donorId={donorId} />}
    </CrmMountGate>
  );
}

export function CharityCrmSegmentsMount() {
  return <CrmMountGate>{(config) => <CrmSegmentsPage config={config} />}</CrmMountGate>;
}

export function CharityCrmNewSegmentMount() {
  return <CrmMountGate>{(config) => <CrmSegmentBuilderPage config={config} />}</CrmMountGate>;
}

export function CharityCrmSegmentMount() {
  const segmentId = useParams({ strict: false }).segmentId ?? '';
  return (
    <CrmMountGate>
      {(config) => <CrmSegmentBuilderPage config={config} segmentId={segmentId} />}
    </CrmMountGate>
  );
}

export function CharityCrmImportMount({
  initialFile,
}: {
  /** Preview seam: open on a file already chosen. */
  initialFile?: { name: string; text: string };
} = {}) {
  return (
    <CrmMountGate>
      {(config) => <CrmImportPage config={config} initialFile={initialFile} />}
    </CrmMountGate>
  );
}
